/** One migration's runs, newest first: mode, outcome, who, when, error and the result it returned. */
import { useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Empty,
  List,
  Pagination,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { MIGRATION_RUNS_LIMIT, RUN_STATE_LABELS, modeLabel } from '../constants';
import {
  migrationErrorMessage,
  runsTotal,
  useMigrationRuns,
  type MigrationRun,
} from '../api/migrationsClient';
import { formatTime } from '../../envCopy/format';
import { runnerName } from '../format';

export const RunStateTag: React.FC<{ status: string }> = ({ status }) => {
  const s = RUN_STATE_LABELS[status as keyof typeof RUN_STATE_LABELS] ?? {
    label: status,
    color: 'default',
  };
  return <Tag color={s.color}>{s.label}</Tag>;
};

const RunEntry: React.FC<{ run: MigrationRun }> = ({ run }) => (
  <List.Item>
    <Space direction="vertical" size={6} style={{ width: '100%' }}>
      <Space size={8} wrap>
        <Typography.Text strong>{modeLabel(run.mode)}</Typography.Text>
        <RunStateTag status={run.status} />
      </Space>
      <Descriptions size="small" column={1}>
        <Descriptions.Item label="Started by">{runnerName(run)}</Descriptions.Item>
        <Descriptions.Item label="Started">{formatTime(run.startedAt)}</Descriptions.Item>
        {run.finishedAt && (
          <Descriptions.Item label="Finished">{formatTime(run.finishedAt)}</Descriptions.Item>
        )}
        <Descriptions.Item label="Version">{run.codeVersion}</Descriptions.Item>
      </Descriptions>
      {run.errorMessage && <Alert type="error" showIcon message={run.errorMessage} />}
      {run.result && (
        <pre
          aria-label="Result"
          style={{ margin: 0, padding: 8, background: '#fafafa', overflowX: 'auto' }}
        >
          {JSON.stringify(run.result, null, 2)}
        </pre>
      )}
    </Space>
  </List.Item>
);

/**
 * Mounted only while the drawer is open, so every open reads the history afresh from page 1.
 * A page reads itself again while it shows a run in progress; the page above also refreshes it
 * after a start and when the list shows this migration's last run changed.
 */
const RunsBody: React.FC<{ id: string }> = ({ id }) => {
  const [page, setPage] = useState(1);
  const query = useMigrationRuns(id, (page - 1) * MIGRATION_RUNS_LIMIT);
  const loaded = query.data?.data;
  const runs = Array.isArray(loaded) ? loaded : undefined;
  const total = runsTotal(query.data);
  const pager =
    total !== undefined && total > MIGRATION_RUNS_LIMIT ? (
      <Pagination
        size="small"
        current={page}
        pageSize={MIGRATION_RUNS_LIMIT}
        total={total}
        showSizeChanger={false}
        showTotal={(n) => `${n} runs`}
        onChange={setPage}
      />
    ) : null;
  if (query.isLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 48 }} />;
  }
  if (!runs) {
    return (
      <Alert
        type="error"
        showIcon
        message={migrationErrorMessage(query.error, 'Could not load the runs.')}
        action={
          <Space>
            <Button size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
            {page > 1 && (
              <Button size="small" onClick={() => setPage(1)}>
                Newest runs
              </Button>
            )}
          </Space>
        }
      />
    );
  }
  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {query.isError && (
        <Alert type="warning" showIcon message="Could not refresh. Showing the runs last loaded." />
      )}
      {runs.length === 0 && page === 1 ? (
        <Empty description="Not run on this environment yet." />
      ) : (
        <List dataSource={runs} rowKey="runId" renderItem={(run) => <RunEntry run={run} />} />
      )}
      {pager}
    </Space>
  );
};

export const MigrationRunsDrawer: React.FC<{
  id: string | null;
  onClose: () => void;
}> = ({ id, onClose }) => (
  <Drawer
    title={id ? `Runs of ${id}` : 'Runs'}
    placement="right"
    width={560}
    open={!!id}
    onClose={onClose}
    destroyOnHidden
  >
    {id && <RunsBody id={id} />}
  </Drawer>
);
